import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
  OpenInNew as OpenInNewIcon,
  ListAlt as TravauxIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Projet, ProjetCreate } from '../../../types/projet';
import { Service, ServiceCreate } from '../../../types/service';
import { projetsAPI, servicesAPI, clientsAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import PermissionButton from '../../../components/PermissionButton';
import { usePermissions } from '../../../contexts/PermissionContext';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import { extractErrorMessage } from '../../../utils/errorHandler';
import CustomTablePagination from '../../../components/CustomTablePagination';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import ComptoirTravauxTable from '../components/ComptoirTravauxTable';

const PRODUCTION_COLOR = '#FF5722';

const PS_FILTER_SCHEMA = {
  date_validation: { type: 'dateRange',    paramFrom: 'date_validation_from', paramTo: 'date_validation_to' },
  code:            { type: 'text',         param: 'code' },
  devis_reference: { type: 'text',         param: 'devis_reference' },  // projets only (services have no devis)
  type:            { type: 'select',       param: 'type' },  // client-side: controls which API is called
  libelle:         { type: 'text',         param: 'libelle' },
  client_id:       { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  date_debut:      { type: 'dateRange',    paramFrom: 'date_debut_from', paramTo: 'date_debut_to' },
  date_fin:        { type: 'dateRange',    paramFrom: 'date_fin_from',   paramTo: 'date_fin_to' },
} as const;

type ItemType = 'projet' | 'service';

interface UnifiedItem {
  id: number;
  type: ItemType;
  code: string;
  devis_id?: number | null; // projet created from a devis: its client is fixed
  devis_reference?: string;
  libelle: string;
  client_id?: number;
  client_name?: string;
  date_debut?: string;
  date_fin?: string;
  date_validation: string;
  is_active: boolean;
  deleted_at?: string;
  origine?: 'comptoir' | null; // managed from Comptoir › Services & Projets: no edit / delete here
  comptoir_service_id?: number | null;
}

const ProjetServiceManagement: React.FC = () => {
  const [items, setItems] = useState<UnifiedItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedItems, setDeletedItems] = useState<UnifiedItem[]>([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState<UnifiedItem | null>(null);
  // Comptoir row whose travaux are shown read-only (désignations and their lines 2.1, 2.2…)
  const [travauxOf, setTravauxOf] = useState<UnifiedItem | null>(null);
  const [currentItem, setCurrentItem] = useState<{
    type: ItemType;
    code: string;
    libelle: string;
    client_id: number | null;
    date_debut: string;
    date_fin: string;
  }>({
    type: 'projet',
    code: '',
    libelle: '',
    client_id: null,
    date_debut: '',
    date_fin: '',
  });
  const [clients, setClients] = useState<Array<{ id: number; name: string; code: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canOpenComptoir = hasPermission('Comptoir', 'view', 'Services & Projets');
  // Editing a projet created from a devis: the client comes from the devis
  const clientLocked = editMode && currentItem.type === 'projet' && !!editingItem?.devis_id;

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    PS_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  useEffect(() => {
    if (tabValue === 0) {
      fetchData();
    }
    fetchClients();
    // Pagination is client-side, so page/rowsPerPage don't trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, filters]);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAllForDropdown();
      setClients(response || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all matching rows; pagination is done client-side so projets and
      // services can be ordered globally (not within per-page windows).
      const skip = 0;
      const limit = 1000;

      // `type` is a client-side filter that picks which APIs to call.
      // Everything else gets forwarded as query params via the `extra` bag.
      const { type, ...extra } = toQueryParams() as Record<string, any>;
      const fetchProjets = !type || type === 'projet';
      // Services have no devis, so filtering by N° Devis must exclude them entirely.
      const fetchServices = (!type || type === 'service') && !extra.devis_reference;

      const [projetsData, servicesData] = await Promise.all([
        fetchProjets
          ? projetsAPI.getAll(skip, limit, undefined, undefined, undefined, undefined, undefined, undefined, extra)
          : Promise.resolve({ items: [], total: 0 } as any),
        fetchServices
          ? servicesAPI.getAll(skip, limit, undefined, undefined, extra)
          : Promise.resolve({ items: [], total: 0 } as any),
      ]);

      const projetsItems: UnifiedItem[] = (projetsData.items || []).map((p: Projet) => ({
        id: p.id,
        type: 'projet' as ItemType,
        code: p.code,
        devis_id: p.devis_id ?? null,
        devis_reference: p.devis_reference || '',
        origine: p.origine,
        comptoir_service_id: p.comptoir_service_id,
        libelle: p.nom,
        client_id: p.client_id,
        client_name: p.client_name || '',
        date_debut: p.date_debut || '',
        date_fin: p.date_fin || '',
        date_validation: p.created_at,
        is_active: p.is_active,
        deleted_at: p.deleted_at,
      }));

      const servicesItems: UnifiedItem[] = (servicesData.items || []).map((s: Service) => ({
        id: s.id,
        type: 'service' as ItemType,
        code: s.code,
        libelle: s.name,
        devis_reference: s.devis_reference || '',
        origine: s.origine,
        comptoir_service_id: s.comptoir_service_id,
        client_id: s.client_id,
        client_name: s.client_name || '',
        date_debut: s.date_debut || '',
        date_fin: s.date_fin || '',
        date_validation: s.created_at,
        is_active: s.is_active,
        deleted_at: s.deleted_at,
      }));

      // Projets and services interleaved, newest first (then N° desc), so a fresh
      // service isn't buried behind every projet on the client-side pages.
      const allItems = [...projetsItems, ...servicesItems].sort(
        (a, b) =>
          new Date(b.date_validation || 0).getTime() - new Date(a.date_validation || 0).getTime() ||
          b.code.localeCompare(a.code, undefined, { numeric: true })
      );

      setTotalCount(allItems.length);
      setItems(allItems);
    } catch (error) {
      showError('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedData = async () => {
    try {
      setLoading(true);
      // Fetch all deleted items (client-side pagination)
      const [projetsData, servicesData] = await Promise.all([
        projetsAPI.getDeleted(),
        servicesAPI.getDeleted(),
      ]);

      const projetsItems: UnifiedItem[] = (projetsData.items || []).map((p: Projet) => ({
        id: p.id,
        type: 'projet' as ItemType,
        code: p.code,
        devis_reference: p.devis_reference || '',
        origine: p.origine,
        libelle: p.nom,
        client_id: p.client_id,
        client_name: p.client_name || '',
        date_debut: p.date_debut || '',
        date_fin: p.date_fin || '',
        date_validation: p.created_at,
        is_active: p.is_active,
        deleted_at: p.deleted_at,
      }));

      const servicesItems: UnifiedItem[] = (servicesData.items || []).map((s: Service) => ({
        id: s.id,
        type: 'service' as ItemType,
        code: s.code,
        libelle: s.name,
        devis_reference: s.devis_reference || '',
        origine: s.origine,
        client_id: s.client_id,
        client_name: s.client_name || '',
        date_debut: s.date_debut || '',
        date_fin: s.date_fin || '',
        date_validation: s.created_at,
        is_active: s.is_active,
        deleted_at: s.deleted_at,
      }));

      const allDeleted = [...projetsItems, ...servicesItems].sort(
        (a, b) => new Date(b.deleted_at || '').getTime() - new Date(a.deleted_at || '').getTime()
      );

      setDeletedItems(allDeleted);
    } catch (error) {
      showError('Erreur lors du chargement des éléments supprimés');
      console.error('Error fetching deleted data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item: UnifiedItem) => {
    try {
      setLoading(true);
      if (item.type === 'projet') {
        await projetsAPI.restore(item.id);
      } else {
        await servicesAPI.restore(item.id);
      }
      showSuccess(`${item.type === 'projet' ? 'Projet' : 'Service'} restauré avec succès`);
      fetchDeletedData();
      fetchData();
    } catch (error) {
      showError('Erreur lors de la restauration');
      console.error('Error restoring item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedData();
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpen = async (item?: UnifiedItem) => {
    if (item) {
      setEditingItem(item);
      setCurrentItem({
        type: item.type,
        code: item.code,
        libelle: item.libelle,
        client_id: item.client_id || null,
        date_debut: item.date_debut || '',
        date_fin: item.date_fin || '',
      });
      setEditMode(true);
    } else {
      setEditingItem(null);
      // Fetch next auto-generated code for projet by default
      try {
        const { code } = await projetsAPI.getNextCode();
        setCurrentItem({
          type: 'projet',
          code: code,
          libelle: '',
          client_id: null,
          date_debut: '',
          date_fin: '',
        });
      } catch (error) {
        console.error('Error fetching next code:', error);
        setCurrentItem({
          type: 'projet',
          code: '',
          libelle: '',
          client_id: null,
          date_debut: '',
          date_fin: '',
        });
      }
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleTypeChange = async (newType: ItemType) => {
    try {
      const { code } = newType === 'projet'
        ? await projetsAPI.getNextCode()
        : await servicesAPI.getNextCode();
      setCurrentItem({
        ...currentItem,
        type: newType,
        code: code,
        libelle: '',
        client_id: null,
        date_debut: '',
        date_fin: '',
      });
    } catch (error) {
      console.error('Error fetching next code:', error);
      setCurrentItem({
        ...currentItem,
        type: newType,
        code: '',
        libelle: '',
        client_id: null,
        date_debut: '',
        date_fin: '',
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingItem(null);
    setCurrentItem({
      type: 'projet',
      code: '',
      libelle: '',
      client_id: null,
      date_debut: '',
      date_fin: '',
    });
    setEditMode(false);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!currentItem.code?.trim() || !currentItem.libelle?.trim()) {
      showError('Le code et le libellé sont obligatoires');
      return;
    }

    // Client and dates are required for projets only; optional for services
    if (currentItem.type === 'projet') {
      if (!currentItem.client_id) {
        showError('Le client est obligatoire');
        return;
      }

      if (!currentItem.date_debut) {
        showError('La date de début est obligatoire');
        return;
      }

      if (!currentItem.date_fin) {
        showError('La date de fin est obligatoire');
        return;
      }
    }

    // Validate date range when both dates are provided
    if (currentItem.date_debut && currentItem.date_fin &&
        new Date(currentItem.date_fin) < new Date(currentItem.date_debut)) {
      showError('La date de fin doit être supérieure ou égale à la date de début');
      return;
    }

    try {
      setLoading(true);

      if (currentItem.type === 'projet') {
        const projetData: ProjetCreate = {
          code: currentItem.code,
          nom: currentItem.libelle,
          client_id: currentItem.client_id!,
          date_debut: currentItem.date_debut,
          date_fin: currentItem.date_fin,
          is_active: true,
        };

        if (editMode && editingItem) {
          await projetsAPI.update(editingItem.id, projetData);
          showSuccess('Projet modifié avec succès');
        } else {
          await projetsAPI.create(projetData);
          showSuccess('Projet créé avec succès');
        }
      } else {
        const serviceData: ServiceCreate = {
          code: currentItem.code,
          name: currentItem.libelle,
          description: '',
          client_id: currentItem.client_id ?? null,
          date_debut: currentItem.date_debut || null,
          date_fin: currentItem.date_fin || null,
          is_active: true,
        };

        if (editMode && editingItem) {
          await servicesAPI.update(editingItem.id, serviceData);
          showSuccess('Service modifié avec succès');
        } else {
          await servicesAPI.create(serviceData);
          showSuccess('Service créé avec succès');
        }
      }

      await fetchData();
      handleClose();
    } catch (err: any) {
      console.error('Save error:', err);
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: UnifiedItem) => {
    const typeLabel = item.type === 'projet' ? 'projet' : 'service';
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer ce ${typeLabel} "${item.libelle}" ?`,
      async () => {
        try {
          setLoading(true);
          if (item.type === 'projet') {
            await projetsAPI.delete(item.id);
          } else {
            await servicesAPI.delete(item.id);
          }
          showSuccess(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} supprimé avec succès`);
          await fetchData();
        } catch (error) {
          showError('Erreur lors de la suppression');
          console.error('Error deleting item:', error);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getTypeChip = (type: ItemType) => {
    return type === 'projet' ? (
      <Chip label="Projet" size="small" sx={{ bgcolor: '#FF5722', color: 'white' }} />
    ) : (
      <Chip label="Service" size="small" sx={{ bgcolor: '#2196F3', color: 'white' }} />
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">Gestion Projets / Services</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="Production"
              operation="add"
              miniModuleName="Projets"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}
            >
              Nouveau
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={{ ...getModuleTableTheme('production'), overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date Validation
                      <ColumnFilter type="dateRange" value={filters.date_validation} onChange={(v) => update('date_validation', v)} label="date validation" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      N° P/S
                      <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      N° Devis
                      <ColumnFilter type="text" value={filters.devis_reference} onChange={(v) => update('devis_reference', v)} label="N° devis" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Type
                      <ColumnFilter
                        type="select"
                        value={filters.type}
                        onChange={(v) => update('type', v)}
                        label="type"
                        color={PRODUCTION_COLOR}
                        options={[
                          { value: 'projet', label: 'Projet' },
                          { value: 'service', label: 'Service' },
                        ]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Libellé
                      <ColumnFilter type="text" value={filters.libelle} onChange={(v) => update('libelle', v)} label="libellé" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.client_id}
                        onChange={(v) => update('client_id', v)}
                        label="client"
                        color={PRODUCTION_COLOR}
                        options={clients.map(c => ({ value: c.id.toString(), label: `${c.code} - ${c.name}` }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date Début
                      <ColumnFilter type="dateRange" value={filters.date_debut} onChange={(v) => update('date_debut', v)} label="date début" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date Fin
                      <ColumnFilter type="dateRange" value={filters.date_fin} onChange={(v) => update('date_fin', v)} label="date fin" color={PRODUCTION_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={`${item.type}-${item.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.date_validation)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.code}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.devis_reference || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{getTypeChip(item.type)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.origine === 'comptoir' ? (
                        <Tooltip title="Voir les travaux demandés">
                          <Box
                            component="button" type="button" onClick={() => setTravauxOf(item)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0, border: 0, bgcolor: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left',
                                  '&:hover .libelle': { color: PRODUCTION_COLOR, textDecoration: 'underline' } }}
                          >
                            <TravauxIcon sx={{ fontSize: 16, color: PRODUCTION_COLOR }} />
                            <Box className="libelle" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.libelle}</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <TruncatedTextWithTooltip text={item.libelle} maxWidth={300} />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TruncatedTextWithTooltip text={item.client_name} maxWidth={200} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.date_debut ? new Date(item.date_debut).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.date_fin ? new Date(item.date_fin).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {item.origine === 'comptoir' ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {/* Managed from the Comptoir: same icons, greyed out (the backend refuses these too).
                            Compact so three buttons fit the width of the usual two and the table doesn't widen. */}
                        <Tooltip title="Géré depuis le Comptoir">
                          <span>
                            <IconButton disabled size="small" sx={{ p: 0.5 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Géré depuis le Comptoir">
                          <span>
                            <IconButton disabled size="small" sx={{ p: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {canOpenComptoir && item.comptoir_service_id && (
                          <Tooltip title="Ouvrir dans Comptoir › Services & Projets">
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              color="primary"
                              onClick={() => navigate(`/comptoir/services/${item.comptoir_service_id}`)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <PermissionButton
                          moduleName="Production"
                          operation="edit"
                          miniModuleName="Projets"
                          component="IconButton"
                          onClick={() => handleOpen(item)}
                          color="primary"
                        >
                          <EditIcon />
                        </PermissionButton>
                        <PermissionButton
                          moduleName="Production"
                          operation="delete"
                          miniModuleName="Projets"
                          component="IconButton"
                          onClick={() => handleDelete(item)}
                          color="error"
                        >
                          <DeleteIcon />
                        </PermissionButton>
                      </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Aucun projet ou service trouvé
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={{ ...getModuleTableTheme('production'), overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Date Validation</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>N° P/S</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Libellé</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Date Suppression</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={`${item.type}-${item.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.date_validation)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.code}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{getTypeChip(item.type)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TruncatedTextWithTooltip text={item.libelle} maxWidth={300} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.deleted_at ? formatDate(item.deleted_at) : '-'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {item.origine === 'comptoir' ? (
                        <Tooltip title="Se restaure avec son offre, dans Comptoir › Offres commerciales">
                          <Chip label="Comptoir" size="small" variant="outlined" />
                        </Tooltip>
                      ) : (
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer "${item.libelle}" ?`,
                            () => handleRestore(item),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {deletedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucun élément supprimé
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode
            ? `Modifier ${currentItem.type === 'projet' ? 'le Projet' : 'le Service'}`
            : 'Nouveau Projet / Service'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!editMode && (
              <Grid item xs={12}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={currentItem.type}
                    onChange={(e) => handleTypeChange(e.target.value as ItemType)}
                  >
                    <MenuItem value="projet">Projet</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                variant="filled"
                fullWidth
                label={currentItem.type === 'projet' ? 'N° Projet' : 'N° Service'}
                value={currentItem.code}
                required
                InputProps={{
                  readOnly: true,
                }}
                helperText={editMode ? 'Non modifiable' : 'Généré automatiquement'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Libellé"
                value={currentItem.libelle}
                onChange={(e) => setCurrentItem({ ...currentItem, libelle: e.target.value })}
                required
                multiline
                rows={3}
              />
            </Grid>

            {/* Client Autocomplete */}
            <Grid item xs={12}>
              <Autocomplete
                options={clients}
                disabled={clientLocked}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                value={clients.find(c => c.id === currentItem.client_id) || null}
                onChange={(_, newValue) => {
                  setCurrentItem({ ...currentItem, client_id: newValue?.id || null });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    variant="filled"
                    required={currentItem.type === 'projet'}
                    error={currentItem.type === 'projet' && !currentItem.client_id}
                    helperText={clientLocked ? `Client du devis ${editingItem?.devis_reference || ''}` : undefined}
                  />
                )}
              />
            </Grid>

            {/* Date Debut */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date de début"
                variant="filled"
                value={currentItem.date_debut}
                onChange={(e) => setCurrentItem({ ...currentItem, date_debut: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required={currentItem.type === 'projet'}
                error={currentItem.type === 'projet' && !currentItem.date_debut}
              />
            </Grid>

            {/* Date Fin */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date de fin"
                variant="filled"
                value={currentItem.date_fin}
                onChange={(e) => setCurrentItem({ ...currentItem, date_fin: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required={currentItem.type === 'projet'}
                error={currentItem.type === 'projet' && !currentItem.date_fin}
                inputProps={{
                  min: currentItem.date_debut
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}
          >
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!travauxOf} onClose={() => setTravauxOf(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Travaux demandés
          {travauxOf && (
            <Typography variant="body2" color="text.secondary">{[travauxOf.code, travauxOf.devis_reference, travauxOf.client_name].filter(Boolean).join(' · ')}</Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {travauxOf && <ComptoirTravauxTable type={travauxOf.type} id={travauxOf.id} />}
        </DialogContent>
        <DialogActions>
          {canOpenComptoir && travauxOf?.comptoir_service_id && (
            <Button startIcon={<OpenInNewIcon />} onClick={() => navigate(`/comptoir/services/${travauxOf.comptoir_service_id}`)}>
              Ouvrir dans le Comptoir
            </Button>
          )}
          <Button onClick={() => setTravauxOf(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ProjetServiceManagement;
